import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Building2, Users, DollarSign, ShieldCheck, Link2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import api, { formatApiError } from "@/lib/api";

const LEADS = [
  { v: "biohacker", label: "BioHacker-sourced", hint: "50% first 3 paid months, then 15%" },
  { v: "academy", label: "Academy-sourced", hint: "10% · $10 min / $25 max per month" },
  { v: "pre_existing", label: "Pre-existing academy customer", hint: "Treated as academy-sourced" },
  { v: "disputed", label: "Disputed / pending review", hint: "Held — no share until reviewed" },
];

const STATUS_COLOR = { active: "#22C55E", paused: "#F59E0B", onboarding: "#94A3B8", invited: "#94A3B8", inactive: "#EF4444", archived: "#64748B" };

export default function AcademyDashboard() {
  const [summary, setSummary] = useState(null);
  const [roster, setRoster] = useState(null);
  const [inviteLink, setInviteLink] = useState("");
  const [dlg, setDlg] = useState(null); // { player }
  const [form, setForm] = useState({ lead_source: "academy", coaching_price_usd: "" });
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [s, r] = await Promise.all([api.get("/academy/billing/summary"), api.get("/academy/roster")]);
      setSummary(s.data); setRoster(r.data);
    } catch (er) { toast.error(formatApiError(er)); }
    api.get("/teams/me/invite-link").then((r) => setInviteLink(`${window.location.origin}${r.data.path}`)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const payMembership = async () => {
    try {
      const { data } = await api.post("/academy/checkout", { origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (er) { toast.error(formatApiError(er)); }
  };

  const openActivate = (p) => {
    setDlg(p);
    setForm({ lead_source: p.billing.lead_source || "academy", coaching_price_usd: p.billing.coaching_price_usd || "" });
    setPreview(null);
  };

  const doPreview = async () => {
    try {
      const { data } = await api.post(`/academy/players/${dlg.user_id}/preview`, {
        lead_source: form.lead_source, coaching_price_usd: Number(form.coaching_price_usd || 0),
      });
      setPreview(data.preview);
    } catch (er) { toast.error(formatApiError(er)); }
  };

  const doActivate = async () => {
    setBusy(true);
    try {
      await api.post(`/academy/players/${dlg.user_id}/activate`, {
        lead_source: form.lead_source, coaching_price_usd: Number(form.coaching_price_usd || 0),
      });
      toast.success("Player activated — billing starts this period");
      setDlg(null); load();
    } catch (er) { toast.error(formatApiError(er)); } finally { setBusy(false); }
  };

  const changeStatus = async (p, action) => {
    try {
      await api.post(`/academy/players/${p.user_id}/status`, { action });
      toast.success(`Player ${action}d`); load();
    } catch (er) { toast.error(formatApiError(er)); }
  };

  const closeMonth = async () => {
    if (!window.confirm("Close this month's revenue-share ledger? This snapshots an invoice and advances paid months.")) return;
    try {
      const { data } = await api.post("/academy/billing/close-month");
      toast.success(`Month closed — BioHacker share $${data.invoice.monthly_bh_share_usd}`);
      load();
    } catch (er) { toast.error(formatApiError(er)); }
  };

  if (!summary || !roster) return <div className="text-white/40 p-8 animate-pulse" data-testid="academy-loading">Loading Academy…</div>;
  const m = summary.membership;
  const active = m.status === "active";

  return (
    <div className="p-6 md:p-8 space-y-6" data-testid="academy-dashboard">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] font-black text-amber-400 flex items-center gap-1.5"><Building2 className="h-3.5 w-3.5" /> BioHacker Academy™</div>
          <h1 className="text-2xl font-black text-white mt-1">Academy Dashboard</h1>
        </div>
        {!active && (
          <Button onClick={payMembership} className="bg-amber-400 hover:bg-amber-300 text-[#0b1424] font-black" data-testid="academy-pay-btn">
            Activate membership — ${m.annual_fee_usd}/yr
          </Button>
        )}
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { icon: ShieldCheck, label: "Membership", val: active ? "Active" : "Pending payment", sub: m.paid_through ? `Renews ${m.paid_through.slice(0, 10)}` : `$${m.annual_fee_usd}/year`, color: active ? "#22C55E" : "#F59E0B" },
          { icon: Users, label: "Billable players", val: summary.active_player_count, sub: `${roster.non_billable_count} not billable`, color: "#22D3EE" },
          { icon: DollarSign, label: "Monthly BioHacker share", val: `$${summary.monthly_bh_share_usd}`, sub: summary.held_for_review ? `${summary.held_for_review} held for review` : "Next invoice estimate", color: "#F59E0B" },
          { icon: DollarSign, label: "Closed invoices", val: summary.invoices.length, sub: summary.invoices[0] ? `Last: ${summary.invoices[0].period}` : "None yet", color: "#94A3B8" },
        ].map(({ icon: I, label, val, sub, color }) => (
          <div key={label} className="rounded-xl border border-[var(--ink-border)] p-4" style={{ backgroundColor: "var(--ink-2)" }}>
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider font-bold text-white/40"><I className="h-3.5 w-3.5" style={{ color }} /> {label}</div>
            <div className="text-2xl font-black text-white mt-1" data-testid={`academy-stat-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>{val}</div>
            <div className="text-[11px] text-white/40 mt-0.5">{sub}</div>
          </div>
        ))}
      </div>

      {inviteLink && (
        <div className="rounded-xl border border-cyan-400/20 bg-cyan-400/5 p-4 flex items-center justify-between gap-3 flex-wrap">
          <div className="text-xs text-white/70 flex items-center gap-2 min-w-0"><Link2 className="h-4 w-4 text-cyan-300 shrink-0" /> <span className="truncate">Player invite link: <span className="font-mono text-cyan-200">{inviteLink}</span></span></div>
          <Button size="sm" variant="outline" className="border-cyan-400/40 text-cyan-300 hover:bg-cyan-400/10" onClick={() => { navigator.clipboard.writeText(inviteLink); toast.success("Copied"); }} data-testid="academy-copy-invite">Copy</Button>
        </div>
      )}

      <div className="rounded-xl border border-[var(--ink-border)] overflow-hidden" style={{ backgroundColor: "var(--ink-2)" }}>
        <div className="px-5 py-4 border-b border-[var(--ink-border)] flex items-center justify-between">
          <h2 className="font-black text-white">Roster & player billing</h2>
          <Button size="sm" variant="outline" className="border-white/15 text-white/70 hover:bg-white/5" onClick={closeMonth} data-testid="academy-close-month-btn">Close month</Button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-[10px] uppercase tracking-wider text-white/40 border-b border-[var(--ink-border)]">
              <th className="px-5 py-3">Player</th><th className="px-5 py-3">Status</th><th className="px-5 py-3">Lead source</th>
              <th className="px-5 py-3 text-right">Coaching $/mo</th><th className="px-5 py-3 text-right">BioHacker share</th><th className="px-5 py-3 text-right">Actions</th>
            </tr></thead>
            <tbody>
              {roster.players.length === 0 && <tr><td colSpan={6} className="px-5 py-6 text-white/40">No players yet — share your invite link above.</td></tr>}
              {roster.players.map((p) => {
                const b = p.billing;
                return (
                  <tr key={p.user_id} className="border-b border-[var(--ink-border)] hover:bg-white/[0.02]" data-testid={`academy-player-${p.user_id}`}>
                    <td className="px-5 py-3"><div className="font-semibold text-white">{p.name}</div><div className="text-[11px] text-white/40">{p.email}</div></td>
                    <td className="px-5 py-3"><span className="text-[10px] font-black uppercase px-2 py-0.5 rounded-full" style={{ color: STATUS_COLOR[b.status] || "#94A3B8", backgroundColor: `${STATUS_COLOR[b.status] || "#94A3B8"}1a` }}>{b.status}</span></td>
                    <td className="px-5 py-3 text-white/70 text-xs">{b.lead_source ? LEADS.find((l) => l.v === b.lead_source)?.label : "—"}{b.lead_source === "biohacker" && <div className="text-[10px] text-white/40">{b.paid_months} paid months</div>}</td>
                    <td className="px-5 py-3 text-right font-mono-num text-white/80">{b.coaching_price_usd != null ? `$${b.coaching_price_usd}` : "—"}</td>
                    <td className="px-5 py-3 text-right font-mono-num text-amber-300">{b.bh_share_usd != null ? `$${b.bh_share_usd}` : "—"}</td>
                    <td className="px-5 py-3 text-right space-x-1">
                      {b.status !== "active" && <Button size="sm" className="bg-emerald-500/90 hover:bg-emerald-400 text-[#04121f] font-bold h-7 px-2.5 text-xs" onClick={() => openActivate(p)} data-testid={`academy-activate-${p.user_id}`}>Activate</Button>}
                      {b.status === "active" && <>
                        <Button size="sm" variant="ghost" className="text-amber-300/80 hover:text-amber-200 h-7 px-2 text-xs" onClick={() => changeStatus(p, "pause")} data-testid={`academy-pause-${p.user_id}`}>Pause</Button>
                        <Button size="sm" variant="ghost" className="text-red-400/70 hover:text-red-300 h-7 px-2 text-xs" onClick={() => changeStatus(p, "deactivate")} data-testid={`academy-deactivate-${p.user_id}`}>Deactivate</Button>
                      </>}
                      {["paused", "inactive"].includes(b.status) && <Button size="sm" variant="ghost" className="text-emerald-300/80 hover:text-emerald-200 h-7 px-2 text-xs" onClick={() => changeStatus(p, "reactivate")} data-testid={`academy-reactivate-${p.user_id}`}>Reactivate</Button>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={!!dlg} onOpenChange={(o) => !o && setDlg(null)}>
        <DialogContent className="bg-[var(--ink-1)] border-[var(--ink-border)] text-white max-w-md">
          <DialogHeader><DialogTitle className="font-black">Activate {dlg?.name}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <div className="text-xs font-bold text-white/60 mb-1.5">Lead source (locked after activation)</div>
              <div className="space-y-1.5">
                {LEADS.map((l) => (
                  <label key={l.v} className={`flex items-start gap-2.5 rounded-lg border p-2.5 cursor-pointer ${form.lead_source === l.v ? "border-amber-400/60 bg-amber-400/10" : "border-white/10 bg-white/5"}`} data-testid={`academy-lead-${l.v}`}>
                    <input type="radio" name="lead" className="mt-0.5" checked={form.lead_source === l.v} onChange={() => setForm({ ...form, lead_source: l.v })} />
                    <span><span className="text-sm font-bold text-white block">{l.label}</span><span className="text-[11px] text-white/45">{l.hint}</span></span>
                  </label>
                ))}
              </div>
            </div>
            <div>
              <div className="text-xs font-bold text-white/60 mb-1.5">Monthly coaching charge (your price)</div>
              <Input type="number" min="0" placeholder="e.g. 250" value={form.coaching_price_usd} onChange={(e) => setForm({ ...form, coaching_price_usd: e.target.value })} className="bg-white/5 border-white/15 text-white" data-testid="academy-price-input" />
            </div>
            <Button variant="outline" className="w-full border-white/15 text-white/70 hover:bg-white/5" onClick={doPreview} data-testid="academy-preview-btn">Preview financial impact</Button>
            {preview && (
              <div className="rounded-lg border border-white/10 bg-white/5 p-3 text-xs" data-testid="academy-preview-result">
                <div className="flex justify-between"><span className="text-white/50">BioHacker monthly share</span><span className="font-black text-amber-300">{preview.bh_share_usd != null ? `$${preview.bh_share_usd}` : "Held for review"}</span></div>
                <div className="flex justify-between mt-1"><span className="text-white/50">Academy keeps</span><span className="font-black text-emerald-300">{preview.academy_share_usd != null ? `$${preview.academy_share_usd}` : "—"}</span></div>
                <div className="text-[10px] text-white/35 mt-1.5">{preview.rule}</div>
              </div>
            )}
            <Button className="w-full bg-emerald-500 hover:bg-emerald-400 text-[#04121f] font-black" disabled={busy || !form.coaching_price_usd} onClick={doActivate} data-testid="academy-confirm-activate">
              {busy ? "Activating…" : "Confirm activation"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
